/*
  TNW — autoplay product video.

  Shopify-hosted product videos previously rendered as a poster image
  with a play button that opened a popup (see snippets/product-media).
  Nothing played until two clicks in, and once open the only control
  was a close button. This plays the video inline as soon as it
  scrolls into the gallery, the way a modern PDP is expected to, and
  gives it a real play/pause control.

  Rules that make autoplay actually work rather than silently fail:
    · muted + playsinline are mandatory — iOS Safari and Chrome both
      refuse to autoplay a video with sound, and without playsinline
      iOS takes the video fullscreen.
    · play() returns a promise that rejects when the browser blocks
      it (low power mode, data saver, user setting). We catch that and
      fall back to showing the play button rather than leaving a dead
      frame.
    · Playback pauses when scrolled away, so we never burn battery or
      bandwidth decoding a video nobody is looking at.
    · Once someone presses pause, we stop auto-managing that video for
      the rest of the page. Scroll should not override an explicit
      choice.
    · prefers-reduced-motion disables autoplay entirely; the video
      stays available on the button.
*/

(function () {
  'use strict';

  var REDUCED = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(el) {
    var video = el.querySelector('video');
    if (!video || el.dataset.tnwVideoReady) return;
    el.dataset.tnwVideoReady = '1';

    /* Belt and braces — the attributes are set in Liquid too, but a
       video_tag filter change upstream shouldn't break autoplay. */
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    var btn = el.querySelector('[data-tnw-video-toggle]');
    var userPaused = false;

    function setState(playing) {
      el.classList.toggle('is-playing', playing);
      if (btn) {
        btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
        btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      }
    }

    function attempt() {
      if (userPaused || REDUCED) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          /* Blocked by the browser — surface the control instead. */
          setState(false);
          el.classList.add('is-blocked');
        });
      }
    }

    video.addEventListener('play', function () { setState(true); });
    video.addEventListener('pause', function () { setState(false); });

    if (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (video.paused) {
          userPaused = false;
          el.classList.remove('is-blocked');
          var p = video.play();
          if (p && typeof p.catch === 'function') { p.catch(function () {}); }
        } else {
          userPaused = true;
          video.pause();
        }
      });
    }

    if (REDUCED) {
      setState(false);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      attempt();
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          attempt();
        } else if (!video.paused) {
          /* Not a user pause — leave userPaused alone so it resumes
             when scrolled back into view. */
          video.pause();
        }
      });
    }, { threshold: [0, 0.5, 1] });

    io.observe(el);

    /* A tab switch should stop playback; returning resumes it. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (!video.paused) video.pause();
      } else {
        var r = el.getBoundingClientRect();
        var vis = r.top < window.innerHeight && r.bottom > 0;
        if (vis) attempt();
      }
    });
  }

  function scan(scope) {
    var els = (scope || document).querySelectorAll('[data-tnw-video]');
    for (var i = 0; i < els.length; i++) ready(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  /* Quick view injects its gallery after load; the theme editor and
     variant swaps re-render sections. */
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('tnw:quickview:open', function () { scan(); });

  if (window.MutationObserver) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches('[data-tnw-video]')) ready(n);
          else if (n.querySelectorAll) scan(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
